import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Printer, BookOpen, History } from "lucide-react";
import { getWhitepaper } from "@/api";

export const WhitepaperBody = ({ sections }) => (
  <div className="wp-prose">
    {sections.map((s) => (
      <section key={s.id} id={`wp-${s.id}`} className="scroll-mt-24 mb-12" data-testid={`wp-section-${s.id}`}>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-50 mb-4">{s.title}</h2>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{s.body}</ReactMarkdown>
      </section>
    ))}
  </div>
);

export default function WhitepaperPage() {
  const [doc, setDoc] = useState(null);
  const [version, setVersion] = useState(null);
  useEffect(() => { getWhitepaper(version).then(setDoc).catch(() => setDoc(null)); }, [version]);
  if (!doc) return <div className="pt-32 text-center text-slate-400" data-testid="wp-loading">Loading white paper…</div>;
  const latest = doc.versions[0]?.version;
  const changelog = { id: "changelog", title: `${doc.sections.length + 1}. Changelog`, body: doc.versions.map((v) => `- **v${v.version}** (${v.published_at.slice(0, 10)}): ${v.note}`).join("\n") };
  const sections = [...doc.sections, changelog];
  return (
    <div className="pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-white/10 pb-6 print:border-0">
          <div className="flex items-center gap-4">
            <img src="/brand/flame-128.png" alt="FLAME logo" className="w-16 h-16" />
            <div>
              <div className="font-pixel text-[9px] text-orange-400">OFFICIAL WHITE PAPER</div>
              <h1 className="font-display text-4xl sm:text-5xl font-black">FLAME</h1>
              <div className="text-sm text-slate-400 mt-1" data-testid="wp-version-badge">
                Version {doc.version}{doc.version !== latest ? " (archived)" : ""} · Last updated {doc.published_at.slice(0, 10)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 print:hidden">
            <History size={16} className="text-slate-400" />
            <select data-testid="wp-version-picker" value={doc.version} onChange={(e) => setVersion(e.target.value)}
              className="bg-[#12131C] border border-[#2A2E45] rounded-lg px-2 py-1.5 text-sm">
              {doc.versions.map((v) => <option key={v.version} value={v.version}>{`v${v.version}${v.version === latest ? " (latest)" : ""}`}</option>)}
            </select>
            <button data-testid="wp-print-button" onClick={() => window.print()} className="btn-tactile btn-ghost text-sm flex items-center gap-1.5"><Printer size={14} /> PDF / Print</button>
          </div>
        </div>
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
          <nav className="hidden lg:block print:hidden" data-testid="wp-toc">
            <div className="sticky top-24">
              <div className="font-pixel text-[9px] text-slate-500 mb-3 flex items-center gap-1"><BookOpen size={12} /> CONTENTS</div>
              <ul className="space-y-1.5 text-sm">
                {sections.map((s) => (
                  <li key={s.id}><a href={`#wp-${s.id}`} className="text-slate-400 hover:text-orange-300 transition-colors" data-testid={`wp-toc-${s.id}`}>{s.title}</a></li>
                ))}
              </ul>
            </div>
          </nav>
          <article className="max-w-3xl"><WhitepaperBody sections={sections} /></article>
        </div>
      </div>
    </div>
  );
}
